import { useState } from 'react';
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import toast from 'react-hot-toast';
import { createEvaluatorMutation, listEvaluatorsOptions } from '@/api/generated/@tanstack/react-query.gen';
import type { EvaluatorItem } from '@/api/generated/types.gen';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import { Textarea } from '@/components/ui/textarea';
import { EvalShell, Panel } from '@/components/Evals/EvalShell';
import { pct } from '@/components/Evals/format';
import { cn } from '@/lib/utils';
import { ProviderBadge } from '@/components/Evals/ProviderBadge';

export const Route = createFileRoute('/_auth/evals/evaluators/')({
  component: EvaluatorsPage,
});

function EvaluatorsPage() {
  const query = useQuery(listEvaluatorsOptions());
  const code = (query.data ?? []).filter((e) => e.kind === 'code');
  const llm = (query.data ?? []).filter((e) => e.kind === 'llm');
  return (
    <EvalShell
      title="Evaluators"
      subtitle="Code checks and LLM judges that score every run."
      actions={<NewJudgeDialog />}
    >
      {!query.data ? (
        <Skeleton className="h-80 rounded-3xl" />
      ) : (
        <div className="grid gap-4 md:grid-cols-2">
          <Panel title="LLM judges">
            <EvaluatorList items={llm} />
          </Panel>
          <Panel title="Code checks">
            <EvaluatorList items={code} />
          </Panel>
        </div>
      )}
    </EvalShell>
  );
}

function EvaluatorList({ items }: { items: EvaluatorItem[] }) {
  return (
    <div className="divide-y divide-neutral-100">
      {items.map((ev) => (
        <Link
          key={ev.id}
          to="/evals/evaluators/$evaluatorId"
          params={{ evaluatorId: ev.id }}
          className={cn('block py-2.5 hover:bg-neutral-50', !ev.active && 'opacity-50')}
        >
          <div className="flex items-center justify-between gap-2">
            <p className="text-[14px] font-medium text-neutral-900">{ev.name}</p>
            <span className="flex items-center gap-2 text-[11px] text-neutral-400">
              {ev.current?.llm?.model && <ProviderBadge id={ev.current.llm.model} />}
              {ev.current?.threshold != null && `limit ${ev.current.threshold}`}
              <span>v{ev.version}</span>
            </span>
          </div>
          <p className="text-[12px] text-neutral-500">{ev.description}</p>
          {ev.alignment && (
            <p className="mt-0.5 text-[12px] text-neutral-600">
              {ev.alignment.compared
                ? `${pct(ev.alignment.agreement)} agreement with you on ${ev.alignment.compared} lines`
                : 'Not aligned yet: label lines in runs it has scored'}
            </p>
          )}
        </Link>
      ))}
    </div>
  );
}

type NewJudgeForm = { key: string; name: string; description: string; target: 'line' | 'run' | 'items'; model: string; prompt: string };

function NewJudgeDialog() {
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const form = useForm<NewJudgeForm>({
    defaultValues: { key: '', name: '', description: '', target: 'line', model: 'claude-opus-5-5', prompt: '' },
  });
  const mutation = useMutation({
    ...createEvaluatorMutation(),
    onSuccess: (ev) => {
      toast.success('Judge added');
      setOpen(false);
      queryClient.invalidateQueries();
      navigate({ to: '/evals/evaluators/$evaluatorId', params: { evaluatorId: ev.id } });
    },
    onError: () => toast.error("Couldn't add it"),
  });
  const target = form.watch('target');
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button className="btn-3d h-9 rounded-full px-5">New judge</Button>
      </DialogTrigger>
      <DialogContent className="max-w-xl">
        <DialogHeader>
          <DialogTitle>New LLM judge</DialogTitle>
        </DialogHeader>
        <form
          className="space-y-3"
          onSubmit={form.handleSubmit((v) =>
            mutation.mutate({
              body: {
                key: v.key,
                name: v.name,
                description: v.description,
                target: v.target === 'line' ? 'line' : 'run',
                llm: {
                  model: v.model,
                  prompt: v.prompt,
                  labels: ['good', 'bad'],
                  few_shot: v.target === 'line' ? 8 : 0,
                  output: v.target === 'items' ? 'items' : 'score',
                },
              },
            }),
          )}
        >
          <div className="flex gap-2">
            <Input placeholder="key, e.g. grading_fairness" {...form.register('key', { required: true })} />
            <Input placeholder="Name" {...form.register('name', { required: true })} />
          </div>
          <Input placeholder="What it judges" {...form.register('description')} />
          <div className="flex gap-1">
            {(['line', 'run', 'items'] as const).map((t) => (
              <button
                key={t}
                type="button"
                onClick={() => form.setValue('target', t)}
                className={cn(
                  'h-7 rounded-full px-3 text-[12px] font-medium',
                  target === t ? 'bg-[#AAE73D] text-neutral-900' : 'bg-neutral-100 text-neutral-600',
                )}
              >
                {t === 'line' ? 'Every tutor line' : t === 'run' ? 'Whole session' : 'Checklist'}
              </button>
            ))}
            <Input className="ml-auto h-7 w-44 text-xs" {...form.register('model')} />
          </div>
          <Textarea
            className="min-h-48 font-mono text-[12px]"
            placeholder={
              target === 'line'
                ? 'Prompt. Variables: {labels} {examples} {base_instructions} {script} {transcript}'
                : target === 'run'
                  ? 'Prompt. Variables: {base_instructions} {script} {transcript}; returns a 1-5 score'
                  : 'Prompt. Variables: {base_instructions} {script} {transcript}; returns pass/partial/fail per item (D1 instructions, S1 screen elements, L1 lines)'
            }
            {...form.register('prompt', { required: true })}
          />
          <Button type="submit" disabled={mutation.isPending} className="btn-3d h-9 w-full rounded-full">
            Add judge
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
}
